import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { ConfiguredToastrService } from '../../../..//helper/toastr/configured-toastr.service';
import { CommunicationService } from '../../../../communication/communication.service';
import { ProjectCommunicationService } from '../../../../communication/project-comms.service';
import { DeactivationDialogService } from '../../../../helper/can-deactivate/deactivation-dialog.service';
import {
  ConfirmationDialogComponent
} from '../../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';
import { ProjectChecklist } from '../../../../models/project/project-info.model';
import Utils from '../../../../util';
import { ProjectChecklistService } from '../checklist.service';
import { ChecklistTemplateDialogComponent } from './checklist-template-dialog/checklist-template-dialog.component';

@Component({
  selector: 'app-project-info-checklist-list',
  templateUrl: './checklist-list.component.html',
  styleUrls: [
    './checklist-list.component.scss',
  ]
})
export class ProjectInfoChecklistListComponent extends UserpilotReloader implements OnInit, OnDestroy {

  onDestroyNotifier = new EventEmitter();
  dataChangeNotifier = new EventEmitter();

  checklists: ProjectChecklist[] = [];
  checklistFormTemplates = null;

  trigger: string;

  editIndex: number;
  readOnlyInitialChecklists: boolean;
  editOnlyInitialChecklists: boolean;

  isProjectInput = false;
  isInitialFormUser = false;

  pageDirty = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private checklistService: ProjectChecklistService,
    private translationService: AnessaTranslationService,
    private comService: CommunicationService,
    private dialog: MatDialog,
    private toastrService: ConfiguredToastrService,
    private projectCommService: ProjectCommunicationService,
    private deactivationDialogService: DeactivationDialogService,
  ) {
    super();
  }

  ngOnInit() {
    this.isInitialFormUser = Utils.isInitialFormUser();
    if (this.router.url.includes('project-input')) {
      this.isProjectInput = true;
    }
    this.checklistService.checklistsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      // uncomment if you are sure, that it will load the right stuff right away
      // in my (Kons) experience, that is not the case. So I am loading all lists if they are reloaded.
      // .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.checklists = this.checklistService.getChecklists();
          this.dataChangeNotifier.next(true);
          this.readOnlyInitialChecklists = Utils.readOnlyInitialChecklists();
          this.editOnlyInitialChecklists = Utils.editOnlyInitialChecklists();
        }
      });

    this.checklistService.checklistsSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.checklists = this.checklistService.getChecklists();
          this.dataChangeNotifier.next(true);
        }
      });

    this.checklistService.checklistsModifiedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.checklists = this.checklistService.getChecklists();
          this.dataChangeNotifier.next(true);
        }
      });

    // get templates of the account if available
    this.checklistService.checklistFormTemplatesLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(
        (loaded: boolean) => {
          if (loaded) {
            this.checklistFormTemplates = this.checklistService.getChecklistFormTemplates();
          }
        }
      );

    this.checklistService.checklistFormTemplatesUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.checklistFormTemplates = this.checklistService.getChecklistFormTemplates();
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.projectCommService.projectInputDraftSaveNotifier
      .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
      .subscribe(save => {
        if (save === true) {
          this.pageDirty = false;
        }
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  canDeactivate() {
    if (this.pageDirty && this.deactivationDialogService.getOpenDialogsLength() < 1) {
      return this.deactivationDialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'));
    }
    return true;
  }

  getLastModifiedArray() {
    const result = [];
    for (const checklist of this.checklists) {
      if (checklist.lastModified) {
        result.push(`${this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.LAST_MODIFIED')}: ${String(checklist.lastModified).slice(0, 10)}`);
      } else {
        result.push(null);
      }
    }
    return result;
  }

  onAddChecklist() {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  onEditChecklist(index: number) {
    this.router.navigate(['edit', index], { relativeTo: this.route });
  }

  onRemoveChecklist(index: number) {
    this.checklistService.removeChecklist(index);
  }

  showFormButtons() {
    return this.router.url !== '/project/projectinfo/checklist';
  }

  getAddButtonLabel() {
    if (this.isProjectInput) {
      return 'PROJECT.INITIAL.INPUT_LIST.ADD_INPUTLIST';
    } else {
      return 'PROJECT.PROJECT_INFO.CHECKLIST.ADD_CHECKLIST';
    }
  }

  async onLoadFormTemplateClick() {
    const response: { templateID: string, isTemplateDelete: boolean } = await this.dialog
      .open(ChecklistTemplateDialogComponent, {
        width: '1000px',
        data: {
          templates: this.checklistFormTemplates,
          templateType: 'form'
        }
      }).afterClosed().toPromise();
    if (!response) return;
    const templateID = response.templateID;
    const isTemplateDelete = response.isTemplateDelete;


    if (!isTemplateDelete) {
      // template load
      for (const template of this.checklistFormTemplates) {
        if (template.templateid === templateID) {
          break;
        }
      }
      await this.fetchAndLoadFormTemplate(templateID);
    } else { // delete template
      this.checklistService.onFormTemplateDelete(templateID);
    }
  }

  async fetchAndLoadFormTemplate(templateID: string) {
    if (templateID) {
      if (this.checklists.length > 0) {
        // ask if they are sure.
        const proceed = await this.dialog.open(ConfirmationDialogComponent, {
          data: {
            message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.IMPORT_DATA_WARNING'),
          }
        }).afterClosed().toPromise();
  
        if (proceed) {
          const loadedTemplate = await this.checklistService.loadFormTemplate(templateID);
          if (loadedTemplate) {
            this.checklists = loadedTemplate;
          }
        }
      } else {
        const loadedTemplate = await this.checklistService.loadFormTemplate(templateID);
        if (loadedTemplate) {
          this.checklists = loadedTemplate;
        }
      }
    }
  }

  onConvertToForm() {
    this.checklistService.convertTo('form');
  }

  onConvertToTabs() {
    this.checklistService.convertTo('tabs');
  }
}
